import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Box, IconButton, Stack, TextField, useTheme } from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type { PartnerWithModuleStatusListItemDto } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSetModulePartnersMutation } from "Modules/Api/Mutations/useSetModulePartnersMutation";
import { useModulePartnersQuery } from "Modules/Api/Queries/useModulePartnersQuery";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlModal } from "Shared/Dialogs/BlModal";

interface EditModulePartnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleID?: string;
}

const columnHelper = createColumnHelper<PartnerWithModuleStatusListItemDto>();

export const EditModulePartnersModal = ({
  isOpen,
  onClose,
  moduleID,
}: EditModulePartnersModalProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const [localEnabledIDs, setLocalEnabledIDs] = useState<Set<string>>(
    new Set(),
  );

  const { data: partnersData, isLoading } = useModulePartnersQuery(
    isOpen ? moduleID : undefined,
  );
  const setModulePartnersMutation = useSetModulePartnersMutation();

  useEffect(() => {
    if (partnersData) {
      setLocalEnabledIDs(
        new Set(
          partnersData.items
            .filter((p) => p.isEnabledForModule)
            .map((p) => p.partnerID),
        ),
      );
    }
  }, [partnersData]);

  const filteredPartners = useMemo(() => {
    const partners = partnersData?.items ?? [];
    if (!searchQuery) return partners;
    const query = searchQuery.toLowerCase();
    return partners.filter((partner) =>
      partner.companyName.toLowerCase().includes(query),
    );
  }, [partnersData, searchQuery]);

  const handleToggle = useCallback((partnerID: string) => {
    setLocalEnabledIDs((prev) => {
      const next = new Set(prev);
      if (next.has(partnerID)) {
        next.delete(partnerID);
      } else {
        next.add(partnerID);
      }
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!moduleID) return;
    try {
      await setModulePartnersMutation.mutateAsync({
        moduleID,
        partnerIDs: [...localEnabledIDs],
      });
      toast.success(
        t(Resources.Modules.EditAvailablePartnersModal.SuccessMessage),
      );
      handleClose();
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setLocalEnabledIDs(new Set());
    onClose();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("companyName", {
        header: t(Resources.Modules.EditAvailablePartnersModal.PartnerName),
        enableSorting: false,
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => {
          const partnerID = info.row.original.partnerID;
          const isEnabled = localEnabledIDs.has(partnerID);
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleToggle(partnerID)}
                sx={{
                  color: isEnabled ? theme.palette.success.main : "inherit",
                }}
              >
                {isEnabled ? <CheckIcon /> : <AddIcon />}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [t, handleToggle, localEnabledIDs, theme],
  );

  const table = useReactTable({
    data: filteredPartners,
    columns,
    getRowId: (row) => row.partnerID,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination },
    onPaginationChange: setPagination,
  });

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Modules.EditAvailablePartnersModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSave}
      cancelButtonText={t(Resources.Common.Close)}
      confirmButtonText={t(Resources.Modules.EditAvailablePartnersModal.Save)}
      isLoading={setModulePartnersMutation.isPending}
      maxWidth="600px"
    >
      <Box>
        <Stack
          direction="row"
          spacing={Spacing.S}
          sx={{ marginBottom: Spacing.M }}
        >
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              placeholder={t(
                Resources.Modules.EditAvailablePartnersModal.SearchPlaceholder,
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: (theme) => theme.colors.backgroundTile,
                  height: "32px",
                },
                "& .MuiInputBase-input": {
                  height: "32px",
                  padding: (theme) =>
                    `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: (theme) => theme.colors.backgroundTile,
                  },
              }}
            />
          </Box>
        </Stack>
        <BlTanstackTable table={table} isLoading={isLoading} />
      </Box>
    </BlModal>
  );
};
